/* global React, Icon, Card, CardHead, PageHeader, Badge, Button, Field, Input, Modal, DataList, FilterPanel, EmptyState, Segmented, makeFilter, toast, ProfileAvatar, ProfilePhotoControls */
// Account/profile pages use PlummeBackend as source of truth. Keep Firebase calls in services/backend.jsx.

const ROLE_LABEL_KEYS = { owner: 'mypage.role.owner', admin: 'mypage.role.admin', accountant: 'mypage.role.accountant', viewer: 'mypage.role.viewer' };
// Indexable like a plain map, but resolved through t() at lookup time so it follows the active locale.
const ROLE_LABEL = new Proxy({}, { get: (_target, role) => (ROLE_LABEL_KEYS[role] ? t(ROLE_LABEL_KEYS[role]) : undefined) });
const ROLE_VARIANT = { owner: 'success', admin: 'neutral', accountant: 'muted', viewer: 'muted' };
// Backend domain id -> locale-independent audit domain key. Logic (icons, filters, access scope) uses these keys;
// labels are resolved through t() only when rendering. Unknown backend ids pass through unchanged.
const AUDIT_DOMAIN_KEY = {
  settings: 'settings',
  invoices: 'invoices',
  otherSales: 'sales',
  expenses: 'expenses',
  customers: 'customers',
  products: 'products',
  suppliers: 'suppliers',
  receiptInbox: 'inbox',
  bankStatements: 'bank',
  bankReconciliationPostings: 'bank',
  mileageEntries: 'mileage',
  employees: 'payroll',
  payrollRuns: 'payroll',
  payrollSettings: 'payroll',
  payrollFixedLines: 'payroll',
};
const AUDIT_DOMAIN_LABELED = new Set([...Object.values(AUDIT_DOMAIN_KEY), 'business', 'access']);
function auditDomainLabel(domain) {
  return AUDIT_DOMAIN_LABELED.has(domain) ? t('audit.domain.' + domain) : domain;
}
const PRIVATE_SHORTCUTS = [
  { tab: 'oversikt', icon: 'wallet', titleKey: 'mypage.shortcut.oversikt.title', textKey: 'mypage.shortcut.oversikt.text' },
  { tab: 'forbruk', icon: 'pie', titleKey: 'mypage.shortcut.forbruk.title', textKey: 'mypage.shortcut.forbruk.text' },
  { tab: 'familie', icon: 'users', titleKey: 'mypage.shortcut.familie.title', textKey: 'mypage.shortcut.familie.text' },
  { tab: 'fremtid', icon: 'trendUp', titleKey: 'mypage.shortcut.fremtid.title', textKey: 'mypage.shortcut.fremtid.text' },
  { tab: 'skatt', icon: 'percent', titleKey: 'mypage.shortcut.skatt.title', textKey: 'mypage.shortcut.skatt.text' },
];
function personalFinanceFamilyEnabled(settings = {}) {
  const features = settings.personalFinanceFeatures || {};
  if (typeof features.family === 'boolean') return features.family;
  const selectedUses = settings.onboardingPreferences?.selectedUses;
  if (Array.isArray(selectedUses) && selectedUses.length) return selectedUses.includes('privat-familie') || selectedUses.includes('familie');
  return true;
}

function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
}

function formatAccountDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(PlummeI18n.intlLocale(), { year: 'numeric', month: 'long' });
}

function formatAccountTimestamp(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(PlummeI18n.intlLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function auditLogSearchKey(value) {
  return String(value || '').toLowerCase().trim();
}

function formatAuditSummaryValue(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);
  const parts = [];
  if (value.kind) parts.push(String(value.kind));
  if (Number.isFinite(Number(value.count))) parts.push(t('audit.summary.rows', { count: value.count }));
  if (Number.isFinite(Number(value.keyCount))) parts.push(t('audit.summary.fields', { count: value.keyCount }));
  return parts.join(' - ') || JSON.stringify(value);
}

function normalizeMembership(membership) {
  return {
    businessId: membership.businessId || membership.id || '',
    businessName: membership.businessName || membership.name || membership.businessId || t('mypage.fallback.business'),
    organizationNumber: membership.organizationNumber || membership.orgnr || '',
    clientType: membership.clientType || (membership.privateEconomy ? 'personal' : 'business'),
    privateEconomy: membership.privateEconomy === true || membership.clientType === 'personal',
    role: membership.role || 'viewer',
    status: membership.status || 'active',
  };
}

function accountUserFromBackend(backend) {
  const user = backend.user || {};
  const memberships = (backend.memberships || []).map(normalizeMembership);
  const activeMembership = memberships.find((membership) => membership.businessId === backend.activeBusinessId) || memberships[0] || {};
  return {
    id: user.uid || '',
    email: user.email || '',
    displayName: user.displayName || user.email || '',
    phoneNumber: user.phoneNumber || '', photoURL: user.photoURL || '',
    createdAt: user.metadata?.creationTime || user.metadata?.createdAt || '',
    role: activeMembership.role || 'viewer',
  };
}

function useEmployeePortals(userId, emailVerified = true) {
  const [state, setState] = React.useState({ loading: false, error: '', rows: [] });
  React.useEffect(() => {
    let cancelled = false;
    if (!userId || !window.PlummeBackend?.getEmployeePortals) {
      setState({ loading: false, error: '', rows: [] });
      return () => { cancelled = true; };
    }
    if (emailVerified === false) {
      setState({ loading: false, error: '', rows: [] });
      return () => { cancelled = true; };
    }
    setState((current) => ({ ...current, loading: true, error: '' }));
    window.PlummeBackend.getEmployeePortals()
      .then((rows) => {
        if (!cancelled) setState({ loading: false, error: '', rows: Array.isArray(rows) ? rows : [] });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error?.message || t('mypage.fallback.employerLoadError'), rows: [] });
      });
    return () => { cancelled = true; };
  }, [userId, emailVerified]);
  return state;
}

function EmailVerificationNotice({ email, busy, onSend, onCheck }) {
  if (!email) return null;
  return <div className="alert alert-warning" style={{ marginTop: 16, alignItems: 'flex-start' }}>
    <span className="alert-ico"><Icon name="mail" size={18} /></span>
    <div className="grow">
      <div className="t-h3" style={{ fontSize: 14 }}>{t('sharedText.app.employeePortalVerifyTitle')}</div>
      <div className="t-sm muted" style={{ fontWeight: 600, marginTop: 3 }}>
        {t('mypage.verify.body', { email })}
      </div>
      <div className="row gap-8 wrap" style={{ marginTop: 12 }}>
        <Button size="sm" variant="secondary" icon="send" disabled={busy} onClick={onSend}>{busy ? t('mypage.verify.sending') : t('mypage.verify.resend')}</Button>
        <Button size="sm" icon="check" disabled={busy} onClick={onCheck}>{busy ? t('mypage.verify.checking') : t('mypage.verify.confirmed')}</Button>
      </div>
    </div>
  </div>;
}

function auditRowFromBackend(row) {
  const rawDomains = (row.changedDomainDetails || []).map((entry) => entry.domain).concat(row.changedDomains || []);
  let domains = [...new Set(rawDomains.map((domain) => AUDIT_DOMAIN_KEY[domain] || domain).filter(Boolean))];
  let text = domains.length ? t('audit.event.updated', { domains: domains.map(auditDomainLabel).join(', ') }) : t('audit.event.updatedBusinessData');
  let icon = domains.includes('expenses')
    ? 'receipt'
    : domains.includes('payroll')
      ? 'wallet'
      : domains.includes('invoices')
        ? 'fileText'
        : domains.includes('settings')
          ? 'settings'
          : 'check';
  if (row.type === 'business_created') {
    domains = ['business'];
    text = t('audit.event.businessCreated');
    icon = 'building';
  } else if (row.type === 'business_invite_created') {
    domains = ['access'];
    text = t('audit.event.inviteCreated', { email: row.targetEmail || t('audit.fallback.newUser'), role: ROLE_LABEL[row.role] || row.role || t('audit.fallback.userLower') });
    icon = 'user';
  } else if (row.type === 'business_invite_accepted') {
    domains = ['access'];
    text = t('audit.event.inviteAccepted', { user: row.targetEmail || row.actorEmail || t('audit.fallback.user') });
    icon = 'checkCircle';
  } else if (row.type === 'business_deletion_requested') {
    domains = ['business'];
    text = row.scheduledFor ? t('audit.event.deletionRequestedFor', { date: formatAccountDate(row.scheduledFor) }) : t('audit.event.deletionRequested');
    icon = 'trash';
  } else if (row.type === 'business_deletion_canceled') {
    domains = ['business'];
    text = t('audit.event.deletionCanceled');
    icon = 'refresh';
  }
  const date = new Date(row.createdAt || '');
  const details = (row.changedDomainDetails || []).map((entry) => ({
    domainKey: AUDIT_DOMAIN_KEY[entry.domain] || entry.domain,
    domain: auditDomainLabel(AUDIT_DOMAIN_KEY[entry.domain] || entry.domain),
    before: entry.beforeSummary || formatAuditSummaryValue(entry.before),
    after: entry.afterSummary || formatAuditSummaryValue(entry.after),
  })).filter((entry) => entry.domain);
  return {
    id: row.id,
    type: row.type || 'business_state_updated',
    icon,
    actor: row.actorEmail || t('audit.fallback.unknownUser'),
    actorUserId: row.actorUserId || '',
    role: row.actorRole || 'viewer',
    targetEmail: row.targetEmail || '',
    targetUserId: row.targetUserId || '',
    inviteId: row.inviteId || '',
    source: row.source || '',
    rawRole: row.role || '',
    scheduledFor: row.scheduledFor || '',
    domains: domains.length ? domains : ['business'],
    details,
    text,
    createdAt: row.createdAt || '',
    at: formatAccountTimestamp(row.createdAt),
    year: Number.isNaN(date.getTime()) ? null : date.getFullYear(),
    monthIndex: Number.isNaN(date.getTime()) ? 0 : date.getMonth(),
  };
}

function useActivityLog(activeBusinessId, year) {
  const [state, setState] = React.useState({ loading: false, error: '', rows: [] });
  React.useEffect(() => {
    let cancelled = false;
    if (!activeBusinessId || !window.PlummeBackend?.listBusinessAuditLogs) {
      setState({ loading: false, error: '', rows: [] });
      return () => { cancelled = true; };
    }
    setState((current) => ({ ...current, loading: true, error: '', failed: false }));
    window.PlummeBackend.listBusinessAuditLogs(activeBusinessId, { year, maxCount: 200 })
      .then((rows) => {
        if (!cancelled) setState({ loading: false, error: '', rows: rows || [] });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error?.message || '', failed: true, rows: [] });
      });
    return () => { cancelled = true; };
  }, [activeBusinessId, year]);
  // Rows stay raw in state; AktivitetsloggPage derives display rows on every render so text follows the active locale.
  return state;
}

function MinSidePage({ setRoute, client, setClient }) {
  const backend = window.PlummeBackend ? window.PlummeBackend.useBackend() : { user: null, memberships: [], loading: false };
  const [edit, setEdit] = React.useState(false);
  const [employeePortalOpen, setEmployeePortalOpen] = React.useState(false);
  const [creatingPrivate, setCreatingPrivate] = React.useState(false);
  const [savingPrivateFeatures, setSavingPrivateFeatures] = React.useState(false);
  const [verificationBusy, setVerificationBusy] = React.useState(false);
  const emailVerified = backend.user?.emailVerified === true;
  const needsEmailVerification = Boolean(backend.user && !emailVerified);
  const portals = useEmployeePortals(backend.user?.uid, emailVerified);
  const u = accountUserFromBackend(backend);
  const shownName = u.displayName || t('mypage.fallback.signedInUser');
  const memberships = (backend.memberships || []).map(normalizeMembership);
  const privateClient = memberships.find((membership) => membership.privateEconomy);
  const activeMembership = memberships.find((membership) => membership.businessId === (client || backend.activeBusinessId));
  const isPersonalContext = activeMembership?.privateEconomy === true;
  const businesses = memberships.filter((membership) => !membership.privateEconomy);
  const ownedBusinesses = businesses.filter((membership) => membership.role === 'owner');
  const clientAssignments = businesses.filter((membership) => membership.role !== 'owner');
  const canEditPrivateFeatures = isPersonalContext && privateClient?.businessId && activeMembership?.businessId === privateClient.businessId;
  const familyEnabled = canEditPrivateFeatures ? personalFinanceFamilyEnabled(backend.appState?.settings || {}) : true;
  const privateShortcuts = canEditPrivateFeatures && !familyEnabled ? PRIVATE_SHORTCUTS.filter((shortcut) => shortcut.tab !== 'familie') : PRIVATE_SHORTCUTS;
  const field = (label, value) => <div><div className="t-sm muted" style={{ fontWeight: 700 }}>{label}</div><div className="t-h3" style={{ fontSize: 15, marginTop: 2 }}>{value || '—'}</div></div>;

  const saveProfile = async (data) => {
    try {
      if (!window.PlummeBackend?.updateCurrentUserProfile) throw new Error(t('mypage.err.profileNotConnected'));
      await window.PlummeBackend.updateCurrentUserProfile(data);
      setEdit(false);
      toast(t('mypage.toast.profileUpdated'), 'success');
    } catch (error) {
      toast(error?.message || t('mypage.err.profileUpdateFailed'), 'error');
    }
  };

  const resendVerificationEmail = async () => {
    setVerificationBusy(true);
    try {
      if (!window.PlummeBackend?.resendEmailVerification) throw new Error(t('mypage.err.verifyNotConnected'));
      const sent = await window.PlummeBackend.resendEmailVerification();
      toast(sent === false ? t('mypage.toast.alreadyVerified') : t('mypage.toast.verifyResent'), 'success');
    } catch (error) {
      toast(error?.message || t('mypage.err.verifySendFailed'), 'error');
    } finally {
      setVerificationBusy(false);
    }
  };

  const checkEmailVerification = async () => {
    setVerificationBusy(true);
    try {
      if (!window.PlummeBackend?.reloadEmailVerification) throw new Error(t('mypage.err.verifyStatusFailed'));
      const verified = await window.PlummeBackend.reloadEmailVerification();
      toast(verified ? t('mypage.toast.emailVerified') : t('mypage.toast.stillUnverified'), verified ? 'success' : 'error');
    } catch (error) {
      toast(error?.message || t('mypage.err.verifyStatusFailed'), 'error');
    } finally {
      setVerificationBusy(false);
    }
  };

  const openPrivateClient = (membership, tab) => {
    if (!membership?.businessId) return;
    if (tab) window.__poTab = tab;
    if (setClient) setClient(membership.businessId);
    if (setRoute) setRoute('privatokonomi');
  };

  const openBusinessClient = (membership) => {
    if (!membership?.businessId) return;
    if (setClient) setClient(membership.businessId);
    if (setRoute) setRoute('hjem');
  };

  const createPrivateClient = async () => {
    setCreatingPrivate(true);
    try {
      if (!window.PlummeBackend?.createPersonalFinanceClient) throw new Error(t('mypage.err.personalNotConnected'));
      const membership = await window.PlummeBackend.createPersonalFinanceClient();
      toast(t('mypage.toast.personalCreated'), 'success');
      openPrivateClient(normalizeMembership(membership || {}));
    } catch (error) {
      toast(error?.message || t('mypage.err.personalCreateFailed'), 'error');
    } finally {
      setCreatingPrivate(false);
    }
  };

  const setFamilyEnabled = async (enabled) => {
    if (!canEditPrivateFeatures || !window.PlummeBackend?.updateState) {
      openPrivateClient(privateClient);
      toast(t('mypage.toast.openPersonalForFamily'), 'info');
      return;
    }
    setSavingPrivateFeatures(true);
    try {
      window.PlummeBackend.updateState((state) => {
        const currentSelectedUses = Array.isArray(state.settings?.onboardingPreferences?.selectedUses)
          ? state.settings.onboardingPreferences.selectedUses
          : ['privat-personlig'];
        const selectedSet = new Set(currentSelectedUses.filter((use) => use !== 'familie' && use !== 'privat-familie'));
        if (enabled) selectedSet.add('privat-familie');
        return {
          ...state,
          settings: {
            ...(state.settings || {}),
            onboardingPreferences: {
              ...((state.settings || {}).onboardingPreferences || {}),
              selectedUses: [...selectedSet],
            },
            personalFinanceFeatures: {
              ...((state.settings || {}).personalFinanceFeatures || {}),
              family: enabled,
            },
          },
        };
      }, true);
      toast(enabled ? t('mypage.toast.familyEnabled') : t('mypage.toast.familyDisabled'), 'success');
    } catch (error) {
      toast(error?.message || t('mypage.err.familySaveFailed'), 'error');
    } finally {
      setSavingPrivateFeatures(false);
    }
  };

  if (employeePortalOpen && window.AnsattPortalPage) {
    return <window.AnsattPortalPage onBack={() => setEmployeePortalOpen(false)} setRoute={setRoute} client={client} setClient={setClient} />;
  }

  return <div className="acc-page">
    <Card>
      <PageHeader title={t('nav.myPage')} subtitle={t('acc.sub')} help titleHelp={t('mypage.titleHelp')} card={false}>
        <Button variant="danger" icon="arrowUpRight" onClick={async () => { await window.PlummeBackend?.signOut?.(); setRoute && setRoute('logginn'); }} data-help-title={t('common.signOut')} data-help={t('acc.signOutHelp')}>{t('common.signOut')}</Button>
      </PageHeader>
      {needsEmailVerification && <EmailVerificationNotice email={u.email} busy={verificationBusy} onSend={resendVerificationEmail} onCheck={checkEmailVerification} />}
      <div className="hairline" style={{ margin: '16px 0 18px' }} />
      <div className="emp-head">
        <ProfileAvatar photoURL={u.photoURL} initials={initials(shownName)} />
        <div className="grow" style={{ minWidth: 0 }}>
          <h2 className="t-h1">{shownName}</h2>
          <div className="t-sm muted" style={{ fontWeight: 700 }}>{u.email}</div>
          <div className="mt-8" data-help-title={t('acc.yourRole')} data-help={t('acc.yourRoleHelp')}><Badge variant={ROLE_VARIANT[u.role]} dot>{ROLE_LABEL[u.role]}</Badge></div>
        </div>
        <div className="row gap-10"><Button variant="secondary" icon="pencil" onClick={() => setEdit(true)} data-help={t('helpTopics.profileScope.body')} data-help-title={t('helpTopics.profileScope.title')}>{t('acc.editProfile')}</Button></div>
      </div>
      <div className="hairline" style={{ margin: '18px 0' }} />
      <div className="grid-3" style={{ gap: '18px 40px' }}>
        {field(t('cust.col.email'), u.email)}
        {field(t('cust.col.phone'), u.phoneNumber)}
        {field(t('mypage.field.memberSince'), formatAccountDate(u.createdAt))}
      </div>
    </Card>

    {isPersonalContext && privateClient && window.PersonalFinanceHouseholdCard && <window.PersonalFinanceHouseholdCard personalClient={privateClient} />}

    <Card style={{ marginTop: 28 }}>
      <CardHead
        icon="building"
        title={t('acc.myBusinesses')}
        help={t('acc.myBusinessesHelp')}
        right={<Button variant="secondary" size="sm" icon="plus" onClick={() => window.showOnboarding ? window.showOnboarding('foretak') : toast(t('mypage.toast.onboardingNotLoaded'), 'info')}>{t('app.nobusiness.create')}</Button>}
      />
      {ownedBusinesses.length ? <div className="col gap-10">{ownedBusinesses.map((b) => <button key={b.businessId} type="button" className="row between gap-12" style={{ width: '100%', padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--card)', color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer' }} onClick={() => openBusinessClient(b)} aria-current={activeMembership?.businessId === b.businessId ? 'page' : undefined}>
        <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)' }}><Icon name="building" size={18} /></span>
          <div><div className="t-h3" style={{ fontSize: 14.5 }}>{b.businessName}</div><div className="t-sm muted">{b.organizationNumber ? t('mypage.orgNumber', { orgnr: b.organizationNumber }) : b.businessId}</div></div></div>
        <div className="row gap-10" style={{ alignItems: 'center' }}><Badge variant={ROLE_VARIANT[b.role]}>{ROLE_LABEL[b.role]}</Badge><Icon name="chevRight" size={18} style={{ color: 'var(--muted-2)', flex: 'none' }} /></div>
      </button>)}</div> : <EmptyState acc="doc" title={backend.loading ? t('mypage.biz.loadingTitle') : t('mypage.biz.emptyTitle')} text={backend.loading ? t('mypage.biz.loadingText') : t('mypage.biz.emptyText')} />}
    </Card>

    <Card style={{ marginTop: 28 }}>
      <CardHead icon="users" title={t('mypage.clients.title')} help={t('mypage.clients.help')} right={<Button variant="secondary" size="sm" icon="users" onClick={() => window.showOnboarding?.('regnskapsforer')}>{t('mypage.clients.setup')}</Button>} />
      {clientAssignments.length ? <div className="col gap-10">{clientAssignments.map((b) => <button key={b.businessId} type="button" className="row between gap-12" style={{ width: '100%', padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--card)', color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer' }} onClick={() => openBusinessClient(b)} aria-current={activeMembership?.businessId === b.businessId ? 'page' : undefined}>
        <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)' }}><Icon name="building" size={18} /></span><div><div className="t-h3" style={{ fontSize: 14.5 }}>{b.businessName}</div><div className="t-sm muted">{b.organizationNumber ? t('mypage.orgNumber', { orgnr: b.organizationNumber }) : t('mypage.clients.access')}</div></div></div>
        <div className="row gap-10" style={{ alignItems: 'center' }}><Badge variant={ROLE_VARIANT[b.role]}>{ROLE_LABEL[b.role]}</Badge><Icon name="chevRight" size={18} style={{ color: 'var(--muted-2)', flex: 'none' }} /></div>
      </button>)}</div> : <p className="t-sm muted" style={{ margin: 0, fontWeight: 600 }}>{t('mypage.clients.empty')}</p>}
    </Card>

    <Card style={{ marginTop: 28 }}>
      <CardHead
        icon="sparkle"
        title={t('shell.client.personalEconomy')}
        help={t('acc.personalFinanceHelp')}
        right={privateClient
          ? <Button variant="secondary" size="sm" iconRight="chevRight" onClick={() => openPrivateClient(privateClient)} data-help={t('helpTopics.profileScope.body')} data-help-title={t('helpTopics.profileScope.title')}>{t('acc.goToPersonalFinance')}</Button>
          : <Button variant="secondary" size="sm" icon="plus" disabled={creatingPrivate} onClick={createPrivateClient}>{creatingPrivate ? t('mypage.pf.creating') : t('mypage.pf.create')}</Button>}
      />
      {privateClient ? <React.Fragment>
        <p className="t-sm muted" style={{ marginTop: -8, marginBottom: 14, fontWeight: 600 }}>
          {t('mypage.pf.intro')}
        </p>
        <div className="row between gap-12" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', marginBottom: 12 }}>
          <div className="row gap-12"><span className="icon-chip" style={{ background: 'color-mix(in srgb, #247c98 14%, var(--card))', color: '#247c98' }}><Icon name="wallet" size={18} /></span>
            <div><div className="t-h3" style={{ fontSize: 14.5 }}>{privateClient.businessName}</div><div className="t-sm muted">{t('acc.ownPersonalClient')}</div></div></div>
          <Badge variant="success" dot>{t('common.active')}</Badge>
        </div>
        <div className="row between gap-12" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', marginBottom: 12 }}>
          <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)', flex: 'none' }}><Icon name="users" size={18} /></span>
            <div><div className="t-h3" style={{ fontSize: 14.5 }}>{t('mypage.pf.family')}</div><div className="t-sm muted" style={{ textWrap: 'pretty' }}>{t('mypage.pf.familyText')}</div></div></div>
          <Switch on={familyEnabled} disabled={savingPrivateFeatures} onChange={setFamilyEnabled} />
        </div>
        <div className="grid-2 gap-12">{privateShortcuts.map((shortcut) => <button key={shortcut.tab} type="button" className="row between gap-12" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', textAlign: 'left', cursor: 'pointer', background: 'var(--card)', font: 'inherit', color: 'inherit' }} onClick={() => openPrivateClient(privateClient, shortcut.tab)}>
          <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)', flex: 'none' }}><Icon name={shortcut.icon} size={18} /></span>
            <div><div className="t-h3" style={{ fontSize: 14.5 }}>{t(shortcut.titleKey)}</div><div className="t-sm muted" style={{ textWrap: 'pretty' }}>{t(shortcut.textKey)}</div></div></div>
          <Icon name="chevRight" size={18} style={{ color: 'var(--muted-2)', flex: 'none' }} />
        </button>)}</div>
      </React.Fragment> : <p className="t-sm muted" style={{ marginTop: -8, marginBottom: 0, fontWeight: 600 }}>
        {t('mypage.pf.emptyIntro')}
      </p>}
    </Card>

    <Card style={{ marginTop: 28 }}>
      <CardHead icon="users" title={t('acc.myEmployers')} help={t('acc.myEmployersHelp')} />
      <p className="t-sm muted" style={{ marginTop: -8, marginBottom: 14, fontWeight: 600 }}>{t('acc.employerPortalAccess')}</p>
      {portals.error && <p className="t-sm" style={{ color: 'var(--error)', fontWeight: 700 }}>{portals.error}</p>}
      {portals.rows.length ? <div className="col gap-10">{portals.rows.map((portal) => <div key={`${portal.businessId}-${portal.employee?.id}`} className="row between gap-12 acc-emp-row" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
        <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)' }}><Icon name="wallet" size={18} /></span>
          <div><div className="t-h3" style={{ fontSize: 14.5 }}>{portal.businessName}</div><div className="t-sm muted">{portal.employee?.jobTitle || portal.employee?.name || t('mypage.emp.fallbackRole')}</div></div></div>
        <div className="row gap-10" style={{ alignItems: 'center' }}><Badge variant="success" dot>{t('common.active')}</Badge><Button variant="secondary" size="sm" iconRight="chevRight" onClick={() => {
          window.rememberEmployeePortal?.(portal);
          if (setRoute) setRoute('ansattportal');
          else setEmployeePortalOpen(true);
        }}>{t('pay.myEmployeePage')}</Button></div>
      </div>)}</div> : <EmptyState noVisual title={portals.loading ? t('mypage.emp.loadingTitle') : t('mypage.emp.emptyTitle')} text={portals.loading ? t('mypage.emp.loadingText') : t('mypage.emp.emptyText')} />}
    </Card>

    {edit && <ProfileEditModal user={u} onClose={() => setEdit(false)} onSave={saveProfile} />}
  </div>;
}

function ProfileEditModal({ user, onClose, onSave }) {
  const [f, setF] = React.useState({ displayName: user.displayName, email: user.email, phoneNumber: user.phoneNumber });
  const [busy, setBusy] = React.useState(false);
  const set = (patch) => setF((state) => ({ ...state, ...patch }));
  const save = async () => {
    setBusy(true);
    try { await onSave(f); } finally { setBusy(false); }
  };
  const resetPassword = async () => {
    try {
      if (!window.PlummeBackend?.sendPasswordResetEmail) throw new Error(t('mypage.err.passwordResetNotConnected'));
      await window.PlummeBackend.sendPasswordResetEmail(f.email);
      toast(t('mypage.toast.passwordLinkSent'), 'success');
    } catch (error) {
      toast(error?.message || t('mypage.err.passwordLinkFailed'), 'error');
    }
  };
  return <Modal open onClose={onClose} title={t('acc.editProfile')} max={560}
    footer={<React.Fragment><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button icon="check" disabled={busy} onClick={save}>{busy ? t('mypage.saving') : t('common.save')}</Button></React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <div className="row gap-14" style={{ alignItems: 'center' }}><ProfileAvatar photoURL={user.photoURL} initials={initials(f.displayName || ' ')} />
        <ProfilePhotoControls photoURL={user.photoURL} /></div>
      <Field label={t('cust.form.contactName')}><Input value={f.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder={t('acc.fullName')} /></Field>
      <Field label={t('cust.col.email')}><Input type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} placeholder={t('auth.emailPlaceholder')} /></Field>
      <Field label={t('cust.col.phone')}><Input value={f.phoneNumber} disabled placeholder={t('acc.phoneFromLogin')} /></Field>
      <div className="hairline" style={{ margin: '4px 0' }} />
      <button className="link-reset" style={{ alignSelf: 'flex-start' }} onClick={resetPassword}>{t('acc.changePassword')}</button>
    </div>
  </Modal>;
}

function auditSearchText(entry) {
  return auditLogSearchKey([
    entry.text,
    entry.actor,
    ROLE_LABEL[entry.role] || entry.role,
    entry.targetEmail,
    entry.source,
    entry.type,
    entry.domains.map(auditDomainLabel).join(' '),
    entry.details.map((detail) => `${detail.domain} ${detail.before} ${detail.after}`).join(' '),
  ].join(' '));
}

function isAccessAuditEntry(entry) {
  return entry.type === 'business_invite_created' || entry.type === 'business_invite_accepted' || entry.domains.includes('access');
}

function AuditDetails({ entry }) {
  const meta = [
    [t('audit.meta.time'), entry.at || '-'],
    [t('audit.meta.user'), entry.actor || '-'],
    [t('audit.meta.role'), ROLE_LABEL[entry.role] || entry.role || '-'],
    [t('audit.meta.action'), entry.type || '-'],
    [t('audit.meta.source'), entry.source || '-'],
    [t('audit.meta.invite'), entry.inviteId || ''],
    [t('audit.meta.targetUser'), entry.targetEmail || ''],
  ].filter(([, value]) => value);
  return <div className="col gap-12" style={{ padding: '12px 6px 10px' }}>
    <div className="grid-3" style={{ gap: '12px 24px' }}>{meta.map(([label, value]) => <div key={label}><div className="t-xs muted" style={{ fontWeight: 800 }}>{label}</div><div className="t-sm" style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{value}</div></div>)}</div>
    {entry.details.length ? <div className="col gap-8">
      <div className="t-xs muted" style={{ fontWeight: 800 }}>{t('acc.changes')}</div>
      {entry.details.map((detail) => <div key={detail.domainKey} className="row between gap-12" style={{ alignItems: 'flex-start', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-ms)', background: 'var(--card)' }}>
        <div><div className="t-sm" style={{ fontWeight: 800 }}>{detail.domain}</div><div className="t-xs muted">{t('audit.detail.before')} {detail.before || '-'}</div></div>
        <div className="t-xs muted" style={{ textAlign: 'right' }}>{t('audit.detail.after')} {detail.after || '-'}</div>
      </div>)}
    </div> : <p className="t-sm muted" style={{ margin: 0 }}>{t('acc.noChangeDetails')}</p>}
  </div>;
}

function AktivitetsloggPage({ year }) {
  const backend = window.PlummeBackend ? window.PlummeBackend.useBackend() : { activeBusinessId: null };
  const selectedYear = Number(year || backend.appState?.activeYear || new Date().getFullYear());
  const currentUserId = backend.user?.uid || '';
  const currentUserEmail = auditLogSearchKey(backend.user?.email || '');
  const rawLog = useActivityLog(backend.activeBusinessId, selectedYear);
  // Derived per render (no memo): labels, text and timestamps must follow a locale switch.
  const log = { ...rawLog, rows: rawLog.rows.map(auditRowFromBackend) };
  const [filter, setFilter] = React.useState(() => makeFilter());
  const [search, setSearch] = React.useState('');
  const [scope, setScope] = React.useState('all');
  const [expanded, setExpanded] = React.useState(null);
  const DOMAINS = [...new Set(log.rows.flatMap((row) => row.domains))].sort((a, b) => auditDomainLabel(a).localeCompare(auditDomainLabel(b), PlummeI18n.intlLocale()));
  const ACTORS = [...new Set(log.rows.map((row) => row.actor))].sort();
  const groups = [
    { label: t('acc.area'), options: DOMAINS.map((d) => ({ key: 'd:' + d, label: auditDomainLabel(d) })) },
    { label: t('acc.user'), options: ACTORS.map((a) => ({ key: 'u:' + a, label: a })) },
  ];
  const picked = (pref) => Object.keys(filter.checks).filter((k) => k.startsWith(pref) && filter.checks[k]).map((k) => k.slice(pref.length));
  const selDom = picked('d:'), selUsr = picked('u:');
  const query = auditLogSearchKey(search);
  const isMine = (entry) => entry.actorUserId
    ? entry.actorUserId === currentUserId
    : Boolean(currentUserEmail && auditLogSearchKey(entry.actor) === currentUserEmail);
  const rows = log.rows.filter((entry) => {
    if (entry.year && entry.year !== selectedYear) return false;
    if (entry.monthIndex < filter.months[0] || entry.monthIndex > filter.months[1]) return false;
    if (selDom.length && !entry.domains.some((domain) => selDom.includes(domain))) return false;
    if (selUsr.length && !selUsr.includes(entry.actor)) return false;
    if (scope === 'mine' && !isMine(entry)) return false;
    if (scope === 'others' && isMine(entry)) return false;
    if (scope === 'access' && !isAccessAuditEntry(entry)) return false;
    if (query && !auditSearchText(entry).includes(query)) return false;
    return true;
  });
  return <div>
    <Card>
      <PageHeader title={t('nav.activityLog')} subtitle={t('acc.logSub')} help titleHelp={t('audit.titleHelp')} card={false}>
        <FilterPanel value={filter} onChange={setFilter} onReset={() => setFilter(makeFilter())} period groups={groups} help={t('acc.logFilterHelp')} />
      </PageHeader>
      <div className="hairline" style={{ margin: '16px 0 18px' }} />
      <div className="row between gap-12" style={{ alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ minWidth: 240, flex: '1 1 280px' }}><Input icon="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('audit.search', { year: selectedYear })} /></div>
        <Segmented value={scope} onChange={setScope} options={[
          { value: 'all', label: t('audit.scope.all') },
          { value: 'others', label: t('audit.scope.others') },
          { value: 'mine', label: t('audit.scope.mine') },
          { value: 'access', label: t('audit.scope.access') },
        ]} />
      </div>
      {log.failed && <p className="t-sm" style={{ color: 'var(--error)', fontWeight: 700 }}>{log.error || t('audit.err.loadFailed')}</p>}
      {rows.length ? <DataList rows={rows} rowKey={(entry) => entry.id} onRowClick={(entry) => setExpanded((current) => current === entry.id ? null : entry.id)} expandedKey={expanded} renderExpanded={(entry) => <AuditDetails entry={entry} />}
        columns={[
          { header: t('audit.col.event'), render: (entry) => <div className="row gap-12"><span className="icon-chip" style={{ background: 'var(--panel)', flex: 'none' }}><Icon name={entry.icon} size={18} /></span><div className="t-h3" style={{ fontSize: 14, textWrap: 'pretty' }}>{entry.text}</div></div> },
          { header: <span data-help-title={t('acc.area')} data-help={t('acc.areaHelp')}>{t('acc.area')}</span>, render: (entry) => <div className="row gap-6" style={{ flexWrap: 'wrap' }}>{entry.domains.map((domain) => <Badge key={domain} variant="muted">{auditDomainLabel(domain)}</Badge>)}</div> },
          { header: <span data-help-title={t('acc.user')} data-help={t('acc.userHelp')}>{t('acc.user')}</span>, render: (entry) => <div><div className="t-sm" style={{ fontWeight: 700 }}>{entry.actor}</div><div className="t-xs muted">{ROLE_LABEL[entry.role] || entry.role}</div></div> },
          { header: t('audit.meta.time'), headerRight: true, cellClass: 't-sm muted num', render: (entry) => entry.at },
        ]} /> : <EmptyState noVisual title={log.loading ? t('audit.empty.loadingTitle') : t('audit.empty.noMatchTitle')} text={log.loading ? t('audit.empty.loadingText') : t('audit.empty.noMatchText')} />}
    </Card>
  </div>;
}

function LoggInnPage({ setRoute, initialView }) {
  const [email, setEmail] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [view, setView] = React.useState(initialView || 'login');
  const [resetEmail, setResetEmail] = React.useState('');
  const [resetBusy, setResetBusy] = React.useState(false);
  React.useEffect(() => {
    const h = (event) => setView(event.detail || 'login');
    window.addEventListener('plumme-login-view', h);
    return () => window.removeEventListener('plumme-login-view', h);
  }, []);
  const sub = view === 'forgot'
    ? 'Tilbakestill passordet ditt'
    : view === 'sent'
      ? 'Sjekk e-posten din'
      : 'Enkel bokføring for små foretak';
  const login = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await window.PlummeBackend?.signIn?.(email.trim(), pw);
      setRoute && setRoute('hjem');
    } catch (error) {
      toast(error?.message || 'Kunne ikke logge inn.', 'error');
    } finally {
      setBusy(false);
    }
  };
  const forgot = async (event) => {
    event.preventDefault();
    setResetBusy(true);
    try {
      const target = (resetEmail || email).trim();
      await window.PlummeBackend?.sendPasswordResetEmail?.(target);
      setResetEmail(target);
      setView('sent');
    } catch (error) {
      toast(error?.message || 'Mangler e-postadresse.', 'error');
    } finally {
      setResetBusy(false);
    }
  };
  const openForgot = () => {
    setResetEmail((resetEmail || email).trim());
    setView('forgot');
  };
  return <div className="login-wrap">
    <div className="login-card">
      <div className="login-brand">
        <img src={view === 'forgot' ? 'src/img/PlummaConfused-320.webp' : 'src/img/PlummaWave-320.webp'} alt="" className="login-plum" />
        <div className="t-display login-title">Plumme</div>
        <div className="t-sm muted login-sub">{sub}</div>
      </div>
      {view === 'login' && <React.Fragment>
        <form onSubmit={login}>
          <Field label={t('cust.col.email')} className="mt-16"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} autoComplete="email" /></Field>
          <Field label={t('auth.password')} className="mt-12"><Input type="password" passwordToggle value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" autoComplete="current-password" /></Field>
          <div className="row between" style={{ marginTop: 10 }}>
            <label className="row gap-8 t-sm" style={{ fontWeight: 600, cursor: 'pointer' }}><input type="checkbox" defaultChecked style={{ accentColor: 'var(--plum-600)' }} /> {t('sharedText.auth.remember')}</label>
            <button type="button" className="t-sm login-inline-link" onClick={openForgot}>{t('auth.forgot')}</button>
          </div>
          <Button type="submit" className="btn-block btn-lg" icon="arrowUpRight" disabled={busy || !email || !pw} style={{ marginTop: 18 }}>{busy ? 'Logger inn...' : 'Logg inn'}</Button>
        </form>
        <div className="login-foot t-sm muted">{t('sharedText.auth.noAccount')} <button type="button" className="login-inline-link" onClick={() => toast(t('r1e.acc.createViaBackend'), 'info')}>{t('set.acc.createAccount')}</button></div>
      </React.Fragment>}
      {view === 'forgot' && <form onSubmit={forgot}>
        <p className="t-sm login-copy">{t('sharedText.auth.forgotCopy')}</p>
        <Field label={t('cust.col.email')} className="mt-14"><Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} autoComplete="email" /></Field>
        <Button type="submit" className="btn-block btn-lg" icon="send" disabled={resetBusy || !resetEmail.trim()} style={{ marginTop: 18 }}>{resetBusy ? 'Sender...' : 'Send tilbakestillingslenke'}</Button>
        <button type="button" className="login-back" onClick={() => setView('login')}><Icon name="arrowLeft" size={15} />{t('auth.backToLogin')}</button>
      </form>}
      {view === 'sent' && <div className="login-sent">
        <div className="login-sent-ico"><Icon name="send" size={26} /></div>
        <p className="t-body login-sent-copy">{t('sharedText.acc.sentLinkTo')} <b>{resetEmail}</b>{t('r1e.acc.resetFollowLink')}</p>
        <Button className="btn-block btn-lg" icon="arrowLeft" style={{ marginTop: 18 }} onClick={() => setView('login')}>{t('auth.backToLogin')}</Button>
        <button type="button" className="login-back" onClick={openForgot}>{t('auth.wrongEmail')}</button>
      </div>}
    </div>
  </div>;
}

Object.assign(window, { MinSidePage, AktivitetsloggPage, LoggInnPage });
window.dispatchEvent?.(new CustomEvent('plumme-page-registered', { detail: { pages: ['MinSidePage', 'AktivitetsloggPage', 'LoggInnPage'] } }));
